import { Link, useParams } from 'react-router-dom';
import { isWineLevelImportRow } from '@gwc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { importsApi, type StagingRowView } from '../api/imports';
import { ApiError } from '../lib/api';

type Step = 'parse' | 'validate' | 'resolve' | 'approve' | 'promote';

const STEPS: { key: Step; label: string }[] = [
  { key: 'parse', label: '1. Parse' },
  { key: 'validate', label: '2. Validate' },
  { key: 'resolve', label: '3. Match entities' },
  { key: 'approve', label: '4. Approve rows' },
  { key: 'promote', label: '5. Promote' },
];

export function ImportBatch() {
  const { id = '' } = useParams();
  const qc = useQueryClient();

  const batchQuery = useQuery({
    queryKey: ['imports', id],
    queryFn: () => importsApi.get(id),
    enabled: Boolean(id),
  });

  const stagingQuery = useQuery({
    queryKey: ['imports', id, 'staging'],
    queryFn: () => importsApi.staging(id),
    enabled: Boolean(id) && (batchQuery.data?.rowCount ?? 0) > 0,
  });

  const parseMut = useMutation({
    mutationFn: () => importsApi.parse(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports', id] });
      qc.invalidateQueries({ queryKey: ['imports', id, 'staging'] });
    },
  });

  const validateMut = useMutation({
    mutationFn: () => importsApi.validate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['imports', id, 'staging'] }),
  });

  const resolveMut = useMutation({
    mutationFn: () => importsApi.resolve(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['imports', id, 'staging'] }),
  });

  const approveMut = useMutation({
    mutationFn: () => importsApi.approve(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['imports', id, 'staging'] }),
  });

  const dryRunMut = useMutation({ mutationFn: () => importsApi.promote(id, true) });
  const promoteMut = useMutation({
    mutationFn: () => importsApi.promote(id, false),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports', id] });
      qc.invalidateQueries({ queryKey: ['imports', id, 'staging'] });
    },
  });

  const rollbackMut = useMutation({
    mutationFn: () => importsApi.rollback(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports', id] });
      qc.invalidateQueries({ queryKey: ['imports', id, 'staging'] });
    },
  });

  const batch = batchQuery.data;
  const rows = stagingQuery.data?.items ?? [];

  const counts = {
    validated: rows.filter((r) => r.status === 'validated').length,
    approved: rows.filter((r) => r.status === 'approved').length,
    promoted: rows.filter((r) => r.status === 'promoted').length,
    errors: rows.filter((r) => r.errors && (r.errors as unknown[]).length > 0).length,
    needsReview: rows.filter((r) => {
      const res = r.resolvedEntities as { needsReview?: boolean } | null;
      return res?.needsReview;
    }).length,
    wineLevel: rows.filter((r) => isWineLevel(r)).length,
  };

  return (
    <div className="space-y-8">
      <div>
        <Link to="/imports" className="text-sm text-cream-300/60 hover:text-gold-200">
          ← Import archive
        </Link>
        <h1 className="mt-2 font-display text-3xl text-cream-50">{batch?.filename ?? 'Import'}</h1>
        {batch && (
          <p className="mt-1 text-sm text-cream-300/70">
            {batch.rowCount} staged rows · status <span className="capitalize">{batch.status}</span>
            {batch.sheetName ? ` · sheet ${batch.sheetName}` : ''}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {STEPS.map(({ key, label }) => (
          <span key={key} className="rounded-full border border-ink-800 px-3 py-1 text-xs text-cream-300/70">
            {label}
          </span>
        ))}
      </div>

      <section className="card space-y-4 p-6">
        <h2 className="font-display text-xl text-cream-50">Pipeline</h2>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary text-sm" disabled={parseMut.isPending} onClick={() => parseMut.mutate()}>
            {parseMut.isPending ? 'Parsing…' : 'Parse file into staging'}
          </button>
          <button type="button" className="btn-ghost text-sm" disabled={validateMut.isPending || rows.length === 0} onClick={() => validateMut.mutate()}>
            Validate
          </button>
          <button type="button" className="btn-ghost text-sm" disabled={resolveMut.isPending || rows.length === 0} onClick={() => resolveMut.mutate()}>
            Resolve entities
          </button>
          <button type="button" className="btn-ghost text-sm" disabled={approveMut.isPending || counts.validated === 0} onClick={() => approveMut.mutate()}>
            Approve valid rows
          </button>
          <button type="button" className="btn-ghost text-sm" disabled={dryRunMut.isPending || counts.approved === 0} onClick={() => dryRunMut.mutate()}>
            Dry-run promote
          </button>
          <button type="button" className="btn-primary text-sm" disabled={promoteMut.isPending || counts.approved === 0} onClick={() => promoteMut.mutate()}>
            Promote to live data
          </button>
          <button type="button" className="btn-ghost text-sm text-wine-300" disabled={rollbackMut.isPending} onClick={() => rollbackMut.mutate()}>
            Rollback last promote
          </button>
        </div>

        <PipelineMessage result={parseMut} label="Parse" />
        <PipelineMessage result={validateMut} label="Validate" />
        <PipelineMessage result={resolveMut} label="Resolve" />
        <PipelineMessage result={approveMut} label="Approve" />
        {dryRunMut.data && (
          <pre className="overflow-x-auto rounded bg-ink-900 p-3 text-xs text-cream-300/80">
            {JSON.stringify(dryRunMut.data.report, null, 2)}
          </pre>
        )}
        <PipelineMessage result={promoteMut} label="Promote" />
        <PipelineMessage result={rollbackMut} label="Rollback" />
      </section>

      <section className="card p-6">
        <h2 className="mb-4 font-display text-xl text-cream-50">Staging rows</h2>
        <p className="mb-4 text-sm text-cream-300/60">
          {counts.validated} validated · {counts.approved} approved · {counts.promoted} promoted ·{' '}
          {counts.errors} with errors · {counts.needsReview} need review
          {counts.wineLevel > 0 && <> · {counts.wineLevel} group results (no member)</>}
        </p>
        {stagingQuery.isLoading ? (
          <p className="text-sm text-cream-300/50">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-cream-300/50">Parse the file to load staging rows.</p>
        ) : (
          <ul className="max-h-[480px] divide-y divide-ink-800 overflow-y-auto text-sm">
            {rows.map((row) => (
              <StagingRowItem key={row.id} row={row} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function PipelineMessage({
  result,
  label,
}: {
  result: { isError: boolean; error: unknown; isSuccess: boolean; data: unknown };
  label: string;
}) {
  if (result.isError) {
    return (
      <p className="text-sm text-wine-300">
        {label}: {result.error instanceof ApiError ? result.error.message : 'Failed'}
      </p>
    );
  }
  if (result.isSuccess) {
    return (
      <p className="text-sm text-gold-200">
        {label}: {JSON.stringify(result.data)}
      </p>
    );
  }
  return null;
}

/** A row with no member name: one wine's line on a group results sheet. */
function isWineLevel(row: StagingRowView): boolean {
  const norm = row.normalized ?? {};
  const blank = (v: unknown) => v == null || String(v).trim() === '';
  return isWineLevelImportRow({
    memberFirstName: blank(norm.memberFirstName) ? null : String(norm.memberFirstName),
    memberLastName: blank(norm.memberLastName) ? null : String(norm.memberLastName),
  });
}

function StagingRowItem({ row }: { row: StagingRowView }) {
  const norm = row.normalized ?? {};
  const wineLevel = isWineLevel(row);
  // Shown as the sheet has them ("78.5 pts", "T3"); bare numbers get a unit.
  const bare = (v: unknown) => /^\d+(\.\d+)?$/.test(String(v).trim());
  const group = [
    norm.groupScore ? `${String(norm.groupScore)}${bare(norm.groupScore) ? ' pts' : ''}` : null,
    norm.groupRank ? `${bare(norm.groupRank) ? '#' : ''}${String(norm.groupRank)}` : null,
  ];
  const title = [
    norm.eventTitle,
    norm.wineLetter,
    ...(wineLevel ? ['Group result', ...group] : [norm.memberFirstName, norm.memberLastName]),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="py-3">
      <div className="flex justify-between gap-2">
        <span className="text-cream-100">{title || row.rowRef}</span>
        <span className="capitalize text-cream-300/50">{row.status}</span>
      </div>
      {row.errors && (row.errors as unknown[]).length > 0 && (
        <p className="mt-1 text-xs text-wine-300">{(row.errors as string[]).join('; ')}</p>
      )}
      {(row.resolvedEntities as { needsReview?: boolean } | null)?.needsReview && (
        <p className="mt-1 text-xs text-gold-400">Low-confidence entity match — review before approving</p>
      )}
    </li>
  );
}
